/**
 * PHSIMS Web Admin - Data Provider
 * Author: Iswe Solutions Team
 * 
 * React Admin data provider for FastAPI backend.
 * Includes session management, retry logic, and graceful error handling.
 * 
 * Self-Healing Features:
 * - Retry on network errors (backend restart during deploy)
 * - Only logout on verified authentication errors (not network issues)
 * - Exponential backoff for retries
 */

import { DataProvider } from 'react-admin';
import axios, { AxiosError } from 'axios';

// Use nginx proxy for API calls
// In production: nginx proxies /api to backend:8000
// In development: can use VITE_API_URL env var
const getApiUrl = () => {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  
  // Use /api path - nginx will proxy to backend
  return '/api';
};

const API_URL = getApiUrl();

// Retry configuration for self-healing during deployments
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 10000; // 10 seconds

/**
 * Clear all authentication data from localStorage
 */
const clearAuthData = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth_method');
  // Clear server-side httpOnly cookie (fire-and-forget)
  fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
};

const rememberPostLoginPath = () => {
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (path && path !== '/login') {
    localStorage.setItem('post_login_redirect', path);
  }
};

/**
 * Check if error is a network error (server unreachable during deploy)
 */
const isNetworkError = (error: AxiosError): boolean => {
  return !error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
};

/**
 * Check if error is a real authentication error (not network issue)
 */
const isAuthError = (error: AxiosError): boolean => {
  // Only treat 401 as auth error (token invalid/expired)
  // 403 means authenticated but insufficient permissions — do NOT logout
  // Network errors (no response) could be deploy in progress
  return error.response?.status === 401;
};

/**
 * Sleep helper for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const httpClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: true,
});


// Handle errors with retry logic for self-healing during deploys
httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as any;
    
    // Initialize retry count if not set
    if (!config._retryCount) {
      config._retryCount = 0;
    }
    
    // Network error - backend might be restarting during deploy
    if (isNetworkError(error) && config._retryCount < MAX_RETRIES) {
      config._retryCount++;
      
      // Exponential backoff with jitter
      const delay = Math.min(
        INITIAL_RETRY_DELAY * Math.pow(2, config._retryCount - 1) + Math.random() * 500,
        MAX_RETRY_DELAY
      );
      
      console.log(`[PHSIMS] Network error, retrying in ${Math.round(delay)}ms (attempt ${config._retryCount}/${MAX_RETRIES})`);
      
      // Dispatch custom event for UI to show reconnecting status
      window.dispatchEvent(new CustomEvent('phsims:reconnecting', { 
        detail: { retryCount: config._retryCount, maxRetries: MAX_RETRIES } 
      }));
      
      await sleep(delay);
      return httpClient.request(config);
    }
    
    // Network error after all retries - don't force logout, show error
    if (isNetworkError(error)) {
      window.dispatchEvent(new CustomEvent('phsims:connection-failed'));
      console.warn('[PHSIMS] Backend unreachable after retries - may be updating');
      // Don't logout - let user retry manually
      return Promise.reject(error);
    }
    
    // Real authentication error from server - user needs to re-login
    if (isAuthError(error)) {
      console.log('[PHSIMS] Authentication error from server, redirecting to login');
      clearAuthData();
      rememberPostLoginPath();
      window.location.href = '/login';
    }
    
    // Clear reconnecting status on success or other errors
    window.dispatchEvent(new CustomEvent('phsims:connected'));
    
    return Promise.reject(error);
  }
);

/**
 * Custom data provider for PHSIMS API
 * Implements React Admin data provider interface
 */
const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    
    const response = await httpClient.get(`/${resource}`, {
      params: {
        page,
        perPage,
        sortBy: field,
        sortOrder: order,
        filter: JSON.stringify(params.filter),
      },
    });
    
    // Handle both array response and paginated response
    const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
    const total = Array.isArray(response.data) ? response.data.length : (response.data.total || data.length);
    
    return {
      data: data,
      total: total,
    };
  },
  
  getOne: async (resource, params) => {
    const response = await httpClient.get(`/${resource}/${params.id}`);
    return { data: response.data };
  },
  
  getMany: async (resource, params) => {
    const response = await httpClient.get(`/${resource}`, {
      params: { ids: params.ids },
    });
    return { data: response.data.data || [] };
  },
  
  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    
    const response = await httpClient.get(`/${resource}`, {
      params: {
        page,
        perPage,
        sortBy: field,
        sortOrder: order,
        [params.target]: params.id,
      },
    });
    
    return {
      data: response.data.data || [],
      total: response.data.total || 0,
    };
  },
  
  create: async (resource, params) => {
    const response = await httpClient.post(`/${resource}`, params.data);
    return { data: { ...response.data } };
  },
  
  update: async (resource, params) => {
    const response = await httpClient.put(
      `/${resource}/${params.id}`,
      params.data
    );
    return { data: response.data };
  },
  
  updateMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient.put(`/${resource}/${id}`, params.data)
      )
    );
    return { data: params.ids };
  },
  
  delete: async (resource, params) => {
    await httpClient.delete(`/${resource}/${params.id}`);
    return { data: params.previousData as any };
  },
  
  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) => httpClient.delete(`/${resource}/${id}`))
    );
    return { data: params.ids };
  },
};

export default dataProvider;
